import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { getNotificatieIcon, vraagPushPermissie, slaTokenOp, registreerPushServiceWorker } from '../services/notificatieService';
import { db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

function NotificatieBel() {
  const navigate = useNavigate();
  const { notificaties, ongelezen, markeerGelezen, markeerAllesGelezen, currentUser } = useApp();
  const [open, setOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unavailable';
    return Notification.permission === 'granted' ? 'granted' : (Notification.permission === 'denied' ? 'denied' : 'prompt');
  });
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatTijd = (datum) => {
    if (!datum) return '';
    const d = datum instanceof Date ? datum : new Date(datum);
    const nu = new Date();
    const diffMin = Math.floor((nu - d) / 60000);
    if (diffMin < 1) return 'Zojuist';
    if (diffMin < 60) return `${diffMin} min geleden`;
    const diffUur = Math.floor(diffMin / 60);
    if (diffUur < 24) return `${diffUur}u geleden`;
    const diffDag = Math.floor(diffUur / 24);
    if (diffDag < 7) return `${diffDag}d geleden`;
    return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  };

  const handleKlik = (notif) => {
    if (!notif.gelezen) markeerGelezen(notif.id);
    if (notif.link) navigate(notif.link);
    setOpen(false);
  };

  const handleEnablePush = async () => {
    try {
      await registreerPushServiceWorker();
      // Get VAPID key from Firestore
      let vapidKey = '';
      try {
        const settingsSnap = await getDocs(query(collection(db, 'settings'), where('key', '==', 'vapidKey')));
        if (!settingsSnap.empty) vapidKey = settingsSnap.docs[0].data().value || '';
      } catch (e) { /* no settings yet */ }
      const token = await vraagPushPermissie(vapidKey);
      if (token && token !== 'local-only' && currentUser?.id) {
        await slaTokenOp(currentUser.id, token);
      }
      setPushStatus(Notification.permission);
    } catch (e) {
      console.warn('Push setup mislukt:', e);
    }
  };

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: 'relative', background: 'transparent', border: 'none',
          cursor: 'pointer', fontSize: 20, padding: '4px 8px', borderRadius: 8,
          color: 'inherit', lineHeight: 1,
        }}
        title={`${ongelezen} ongelezen melding${ongelezen !== 1 ? 'en' : ''}`}
      >
        🔔
        {ongelezen > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 2,
            background: '#D13438', color: '#fff', fontSize: 10, fontWeight: 800,
            minWidth: 16, height: 16, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
          }}>
            {ongelezen > 99 ? '99+' : ongelezen}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          width: 340, maxHeight: 440, background: '#fff', borderRadius: 14,
          boxShadow: '0 8px 30px rgba(0,0,0,0.18)', border: '1px solid #edebe9',
          zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.15s ease',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: '1px solid #f3f2f1',
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
              Meldingen {ongelezen > 0 && <span style={{ fontSize: 12, color: '#D13438' }}>({ongelezen})</span>}
            </span>
            {ongelezen > 0 && (
              <button onClick={() => markeerAllesGelezen()}
                style={{ background: 'none', border: 'none', color: '#0078D4', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Alles gelezen
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {(!notificaties || notificaties.length === 0) ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#a19f9d' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                <div style={{ fontSize: 13 }}>Geen meldingen</div>
              </div>
            ) : (
              notificaties.slice(0, 30).map(n => (
                <div
                  key={n.id}
                  onClick={() => handleKlik(n)}
                  style={{
                    display: 'flex', gap: 12, padding: '12px 16px', cursor: 'pointer',
                    background: n.gelezen ? '#fff' : '#F0F6FF',
                    borderBottom: '1px solid #f8f7f6',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = n.gelezen ? '#faf9f8' : '#E8F0FE'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.gelezen ? '#fff' : '#F0F6FF'; }}
                >
                  <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>
                    {getNotificatieIcon(n.type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: n.gelezen ? 500 : 700, color: '#1a1a2e',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {n.titel}
                    </div>
                    <div style={{
                      fontSize: 12, color: '#605e5c', marginTop: 2,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {n.bericht}
                    </div>
                    <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>
                      {formatTijd(n.aangemaakt)}
                      {n.afzenderNaam && ` · ${n.afzenderNaam}`}
                    </div>
                  </div>
                  {!n.gelezen && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', background: '#0078D4',
                      flexShrink: 0, marginTop: 6,
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Push notification banner */}
          {pushStatus === 'prompt' && (
            <div style={{
              padding: '10px 16px', borderTop: '1px solid #edebe9', background: '#fff8f0',
            }}>
              {isIOS && !isStandalone ? (
                <div style={{ fontSize: 11, color: '#7A6400', lineHeight: 1.5 }}>
                  📱 <strong>iOS:</strong> Voeg deze app toe aan je beginscherm voor push notificaties.
                  <br />Tik op <span style={{ fontSize: 13 }}>⬆️</span> → "Zet op beginscherm"
                </div>
              ) : (
                <button onClick={handleEnablePush} style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8,
                  border: '1px solid #E86E1C', background: '#fff', color: '#E86E1C',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                  🔔 Push notificaties inschakelen
                </button>
              )}
            </div>
          )}
          {pushStatus === 'denied' && (
            <div style={{
              padding: '8px 16px', borderTop: '1px solid #edebe9', fontSize: 11,
              color: '#a19f9d', textAlign: 'center',
            }}>
              Push notificaties geblokkeerd — schakel in via browserinstellingen
            </div>
          )}
          {pushStatus === 'granted' && (
            <div style={{
              padding: '8px 16px', borderTop: '1px solid #edebe9', fontSize: 11,
              color: '#107C10', textAlign: 'center',
            }}>
              ✅ Push notificaties actief
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificatieBel;
